// admin/functions/generateWordcloudDraftAI.js
// AI word-cloud-prompt generation, calling OUR backend proxy
// (/generateAiQuestions) — same architecture as the quiz/poll generators,
// adapted to the simplest schema: a list of short prompt strings.
//
// Output is always an EDITABLE DRAFT — never auto-published.

const AI_BATCH_SIZE = 10;
const MAX_EMPTY_RETRIES = 3;

function buildPrompt({ topic, count, excludeTexts }) {
  const exclude =
    excludeTexts && excludeTexts.length
      ? `\n\nEXCLUDE LIST — do not repeat any of these prompts (reword or pick entirely different ones):\n` +
        excludeTexts.map((t) => `- ${t}`).join("\n")
      : "";

  return (
    `You are a JSON generator. Respond with ONLY a valid JSON array of strings — no markdown, no code fences, no explanations, no extra text. ` +
    `Generate exactly ${count} short, engaging WORD CLOUD prompts about "${topic}" suitable for a live audience activity ` +
    `(e.g., "Describe our culture in one word", "What excites you most about this topic?"). ` +
    `Each prompt must be a single string under 150 characters. Vary the phrasing across prompts. Do not duplicate any prompt within this batch.` +
    `${exclude}\n\n` +
    `Respond with only the JSON array of strings. Example shape:\n` +
    `["Prompt one", "Prompt two"]`
  );
}

function stripFences(raw) {
  return String(raw || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .replace(/\\_/g, "_")
    .trim();
}

function parseAiResponse(raw) {
  const cleaned = stripFences(raw);

  if (cleaned.startsWith("[") && cleaned.endsWith("]")) {
    try {
      return JSON.parse(cleaned);
    } catch (e) {
      // fall through
    }
  }

  const arrayMatch = cleaned.match(/\[[\s\S]*\]/);
  if (arrayMatch) {
    try {
      return JSON.parse(arrayMatch[0]);
    } catch (e) {
      // fall through
    }
  }

  // Lenient recovery: pull out quoted strings one by one.
  const strings = [];
  const stringRegex = /"((?:[^"\\]|\\.)*)"/g;
  let m;
  while ((m = stringRegex.exec(cleaned)) !== null) {
    strings.push(m[1].replace(/\\"/g, '"'));
  }
  if (strings.length) return strings;

  throw new Error("AI did not return a parseable JSON array");
}

async function callAiProxy(prompt, token) {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/generateAiQuestions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.success === false) {
    throw new Error(json.message || `AI proxy request failed (${response.status})`);
  }
  return json.response;
}

async function fetchAiBatch({ topic, count, excludeTexts, token }) {
  const prompt = buildPrompt({ topic, count, excludeTexts });
  const raw = await callAiProxy(prompt, token);
  const parsed = parseAiResponse(raw);

  const excludeSet = new Set(excludeTexts.map((t) => t.toLowerCase()));
  const seen = new Set();
  const results = [];

  for (const item of parsed) {
    const text = String(item || "").trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (excludeSet.has(key) || seen.has(key)) continue;
    seen.add(key);
    results.push(text);
  }

  return results;
}

/**
 * Generate `count` word-cloud prompt drafts, batching + retrying
 * short/failed batches until the count is reached or the failure cap is hit.
 *
 * @param {object} args
 * @param {string} args.topic
 * @param {number} args.count
 * @param {string[]} [args.existingTexts]
 * @param {string} args.token
 * @returns {Promise<string[]>}
 */
export async function generateWordcloudDraftAI({ topic, count = 3, existingTexts = [], token }) {
  const collected = [];
  const excludeTexts = [...existingTexts];
  let remaining = count;
  let emptyStreak = 0;

  while (remaining > 0) {
    const batchCount = Math.min(AI_BATCH_SIZE, remaining);
    let batchResults;
    try {
      batchResults = await fetchAiBatch({ topic, count: batchCount, excludeTexts, token });
    } catch (err) {
      emptyStreak += 1;
      if (emptyStreak >= MAX_EMPTY_RETRIES) {
        if (collected.length) return collected;
        throw err;
      }
      continue;
    }

    if (batchResults.length === 0) {
      emptyStreak += 1;
      if (emptyStreak >= MAX_EMPTY_RETRIES) break;
      continue;
    }

    emptyStreak = 0;
    collected.push(...batchResults);
    excludeTexts.push(...batchResults);
    remaining = count - collected.length;
  }

  return collected.slice(0, count);
}

export default generateWordcloudDraftAI;
